import React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { sdk } from '@/lib/sdk'
import { listItems, cn } from '@/lib/utils'
import type { ModuleFieldComponentProps, VisitorsFieldMode, VisitorsModuleConfig } from '@/types/space-wizard'
import { MAX_INSTRUCTIONS, type VisitorsConfigErrors } from './visitorsUtils'

interface Props extends ModuleFieldComponentProps<VisitorsModuleConfig> {
  errors?: VisitorsConfigErrors | null
}

interface Option { id: string; title: string }

const chip = (active: boolean, disabled?: boolean) =>
  cn(
    'rounded-lg px-3 py-2 text-left text-sm transition-colors',
    active ? 'bg-primary-100 text-primary-700' : 'hover:bg-gray-100',
    disabled && 'cursor-not-allowed opacity-50'
  )

/**
 * The Visitors setup (F-83 §4.2): which Presence space receives the arrivals, who registers guests,
 * which details are asked, the e-mails, the arrival instructions and the per-host limit.
 * The wizard and "Configure space" draw this same component (PADROES-UI section 14).
 */
export const VisitorsSpaceFields: React.FC<Props> = ({ config, onChange, disabled = false, errors }) => {
  const { t } = useTranslation('visitors')
  const [spaces, setSpaces] = React.useState<Option[] | null>(null)
  const [groups, setGroups] = React.useState<Option[] | null>(null)
  const whoCanInvite = config.whoCanInvite ?? 'members'
  const fields = config.fields ?? {}
  const emailGuest = config.emailGuest ?? 'off'

  React.useEffect(() => {
    let alive = true
    sdk.spaces.list()
      .then((r: unknown) => {
        if (!alive) return
        const list = listItems<any>(r).filter((s) => s.module === 'attendance').map((s) => ({ id: s.id, title: s.title }))
        setSpaces(list)
      })
      .catch(() => { if (alive) setSpaces([]) })
    sdk.memberGroups.list()
      .then((r: any) => { if (alive) setGroups((Array.isArray(r) ? r : listItems<any>(r)).map((g: any) => ({ id: g.id, title: g.name }))) })
      .catch(() => { if (alive) setGroups([]) })
    return () => { alive = false }
  }, [])

  /* A selector never opens empty (PADROES-UI section 9): the first Presence space is marked from the start. */
  React.useEffect(() => {
    if (spaces && spaces.length > 0 && !config.attendanceSpaceId) onChange({ ...config, attendanceSpaceId: spaces[0].id })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fills the empty choice once the list arrives
  }, [spaces])

  React.useEffect(() => {
    if (whoCanInvite === 'group' && groups && groups.length > 0 && !config.inviterGroupId) onChange({ ...config, inviterGroupId: groups[0].id })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- same: fills the group once it is known
  }, [groups, whoCanInvite])

  const set = (patch: Partial<VisitorsModuleConfig>) => onChange({ ...config, ...patch })
  const setField = (name: 'company' | 'email' | 'phone', mode: VisitorsFieldMode) => set({ fields: { ...fields, [name]: mode } })

  const modes: VisitorsFieldMode[] = ['off', 'optional', 'required']
  const instructions = config.arrivalInstructions ?? ''

  return (
    <div className="space-y-5">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{t('config.attendance.label')}</label>
        {spaces === null ? (
          <div className="h-10 animate-pulse rounded-lg bg-gray-100" />
        ) : spaces.length === 0 ? (
          <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700">{t('config.attendance.none')}</p>
        ) : (
          <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap" role="radiogroup" aria-label={t('config.attendance.label')}>
            {spaces.map((s) => (
              <button key={s.id} type="button" role="radio" aria-checked={config.attendanceSpaceId === s.id} disabled={disabled}
                onClick={() => set({ attendanceSpaceId: s.id })} className={chip(config.attendanceSpaceId === s.id, disabled)}>
                {s.title}
              </button>
            ))}
          </div>
        )}
        {errors?.attendanceSpaceId
          ? <p role="alert" className="text-xs text-red-600">{errors.attendanceSpaceId}</p>
          : <p className="text-xs text-gray-500">{t('config.attendance.hint')}</p>}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{t('config.who.label')}</label>
        <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap" role="radiogroup" aria-label={t('config.who.label')}>
          {(['members', 'staff', 'group'] as const).map((value) => (
            <button key={value} type="button" role="radio" aria-checked={whoCanInvite === value} disabled={disabled}
              onClick={() => set({ whoCanInvite: value })} className={chip(whoCanInvite === value, disabled)}>
              {t(`config.who.${value}`)}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">{t(`config.who.${whoCanInvite}Hint`)}</p>
        {whoCanInvite === 'group' && (
          <div className="space-y-2 pt-1">
            <label className="text-sm font-medium text-gray-700">{t('config.group.label')}</label>
            {groups === null ? (
              <div className="h-10 animate-pulse rounded-lg bg-gray-100" />
            ) : groups.length === 0 ? (
              <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-700">{t('config.group.none')}</p>
            ) : (
              <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap" role="radiogroup" aria-label={t('config.group.label')}>
                {groups.map((g) => (
                  <button key={g.id} type="button" role="radio" aria-checked={config.inviterGroupId === g.id} disabled={disabled}
                    onClick={() => set({ inviterGroupId: g.id })} className={chip(config.inviterGroupId === g.id, disabled)}>
                    {g.title}
                  </button>
                ))}
              </div>
            )}
            {errors?.inviterGroupId && <p role="alert" className="text-xs text-red-600">{errors.inviterGroupId}</p>}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-gray-900">{t('config.fields.title')}</h4>
          <p className="text-xs text-gray-500">{t('config.fields.hint')}</p>
        </div>
        {(['company', 'email', 'phone'] as const).map((name) => (
          <div key={name} className="space-y-2">
            <label className="text-sm font-medium text-gray-700">{t(`config.fields.${name}`)}</label>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t(`config.fields.${name}`)}>
              {modes.map((mode) => (
                <button key={mode} type="button" role="radio" aria-checked={(fields[name] ?? 'optional') === mode} disabled={disabled}
                  onClick={() => setField(name, mode)} className={cn(chip((fields[name] ?? 'optional') === mode, disabled), 'whitespace-nowrap')}>
                  {t(`config.fields.modes.${mode}`)}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-700">{t('config.notifyHost.label')}</p>
          <p className="text-xs text-gray-500">{t('config.notifyHost.hint')}</p>
        </div>
        <Switch checked={config.notifyHostOnRegister ?? true} disabled={disabled} className="data-[state=checked]:bg-primary"
          aria-label={t('config.notifyHost.label')} onCheckedChange={(checked) => set({ notifyHostOnRegister: checked })} />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">{t('config.emailGuest.label')}</label>
        <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap" role="radiogroup" aria-label={t('config.emailGuest.label')}>
          {(['off', 'optional', 'always'] as const).map((value) => (
            <button key={value} type="button" role="radio" aria-checked={emailGuest === value} disabled={disabled}
              onClick={() => set({ emailGuest: value })} className={chip(emailGuest === value, disabled)}>
              {t(`config.emailGuest.${value}`)}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500">{t(`config.emailGuest.${emailGuest}Hint`)}</p>
      </div>

      <div className="space-y-2">
        <label htmlFor="visitors-instructions" className="text-sm font-medium text-gray-700">{t('config.instructions.label')}</label>
        <Textarea id="visitors-instructions" value={instructions} rows={3} maxLength={MAX_INSTRUCTIONS} disabled={disabled}
          placeholder={t('config.instructions.placeholder')} onChange={(e) => set({ arrivalInstructions: e.target.value })}
          className={cn(errors?.arrivalInstructions && 'border-red-500')} aria-invalid={Boolean(errors?.arrivalInstructions)} />
        {errors?.arrivalInstructions
          ? <p role="alert" className="text-xs text-red-600">{errors.arrivalInstructions}</p>
          : <p className="text-xs text-gray-500">{t('config.instructions.hint', { count: instructions.length, max: MAX_INSTRUCTIONS })}</p>}
      </div>

      <div className="space-y-2">
        <label htmlFor="visitors-limit" className="text-sm font-medium text-gray-700">{t('config.limit.label')}</label>
        <Input id="visitors-limit" type="number" inputMode="numeric" disabled={disabled}
          value={config.maxExpectedPerHostPerDay ?? ''} placeholder={t('config.limit.placeholder')}
          onChange={(e) => set({ maxExpectedPerHostPerDay: e.target.value === '' ? null : Number(e.target.value) })}
          className={cn('max-w-[12rem]', errors?.maxExpectedPerHostPerDay && 'border-red-500')}
          aria-invalid={Boolean(errors?.maxExpectedPerHostPerDay)} />
        {errors?.maxExpectedPerHostPerDay
          ? <p role="alert" className="text-xs text-red-600">{errors.maxExpectedPerHostPerDay}</p>
          : <p className="text-xs text-gray-500">{t('config.limit.hint')}</p>}
      </div>
    </div>
  )
}
